import { useState, useEffect } from 'react';
import { Users, Heart, GraduationCap, BookOpen, Star, Baby, Palette, Utensils, ClipboardList, Calendar, CreditCard, UserPlus, FileText, Camera, Share2 } from 'lucide-react';
import FramedPhoto from '../frames/FramedPhoto';
import ShareFrameDialog from '../frames/ShareFrameDialog';
import { useActiveFrames, framesForChild } from '../frames/useActiveFrames';
import { FRAME_META } from '../frames/frameArt';

/**
 * DATA TYPE ICONS FOR IMPORT/EXPORT
 * Modern UI icons to replace emojis
 */
export function ChildrenIcon({ size = 'md', className = '' }) {
  const sizeClasses = {
    xs: 'w-4 h-4', sm: 'w-5 h-5', md: 'w-6 h-6', lg: 'w-8 h-8', xl: 'w-10 h-10', '2xl': 'w-12 h-12',
  };
  return (
    <span className={`inline-flex items-center justify-center relative ${className}`}>
      <Baby className={`${sizeClasses[size]} text-pink-500`} />
      <Heart className="absolute -top-0.5 -right-0.5 w-2 h-2 text-red-400 fill-red-400" />
    </span>
  );
}

export function ActivitiesIcon({ size = 'md', className = '' }) {
  const sizeClasses = {
    xs: 'w-4 h-4', sm: 'w-5 h-5', md: 'w-6 h-6', lg: 'w-8 h-8', xl: 'w-10 h-10', '2xl': 'w-12 h-12',
  };
  return (
    <span className={`inline-flex items-center justify-center relative ${className}`}>
      <Palette className={`${sizeClasses[size]} text-purple-500`} />
      <Star className="absolute -top-0.5 -right-0.5 w-2 h-2 text-amber-400 fill-amber-400" />
    </span>
  );
}

export function NutritionIcon({ size = 'md', className = '' }) {
  const sizeClasses = {
    xs: 'w-4 h-4', sm: 'w-5 h-5', md: 'w-6 h-6', lg: 'w-8 h-8', xl: 'w-10 h-10', '2xl': 'w-12 h-12',
  };
  return (
    <span className={`inline-flex items-center justify-center relative ${className}`}>
      <Utensils className={`${sizeClasses[size]} text-orange-500`} />
    </span>
  );
}

export function DailyLogsIcon({ size = 'md', className = '' }) {
  const sizeClasses = {
    xs: 'w-4 h-4', sm: 'w-5 h-5', md: 'w-6 h-6', lg: 'w-8 h-8', xl: 'w-10 h-10', '2xl': 'w-12 h-12',
  };
  return (
    <span className={`inline-flex items-center justify-center relative ${className}`}>
      <ClipboardList className={`${sizeClasses[size]} text-blue-500`} />
    </span>
  );
}

export function UsersIcon({ size = 'md', className = '' }) {
  const sizeClasses = {
    xs: 'w-4 h-4', sm: 'w-5 h-5', md: 'w-6 h-6', lg: 'w-8 h-8', xl: 'w-10 h-10', '2xl': 'w-12 h-12',
  };
  return (
    <span className={`inline-flex items-center justify-center relative ${className}`}>
      <Users className={`${sizeClasses[size]} text-indigo-500`} />
    </span>
  );
}

export function CalendarIcon({ size = 'md', className = '' }) {
  const sizeClasses = {
    xs: 'w-4 h-4', sm: 'w-5 h-5', md: 'w-6 h-6', lg: 'w-8 h-8', xl: 'w-10 h-10', '2xl': 'w-12 h-12',
  };
  return (
    <span className={`inline-flex items-center justify-center relative ${className}`}>
      <Calendar className={`${sizeClasses[size]} text-teal-500`} />
    </span>
  );
}

export function DevelopmentIcon({ size = 'md', className = '' }) {
  const sizeClasses = {
    xs: 'w-4 h-4', sm: 'w-5 h-5', md: 'w-6 h-6', lg: 'w-8 h-8', xl: 'w-10 h-10', '2xl': 'w-12 h-12',
  };
  return (
    <span className={`inline-flex items-center justify-center relative ${className}`}>
      <BookOpen className={`${sizeClasses[size]} text-green-500`} />
      <Star className="absolute -top-0.5 -right-0.5 w-2 h-2 text-amber-400 fill-amber-400" />
    </span>
  );
}

export function BMIIcon({ size = 'md', className = '' }) {
  const sizeClasses = {
    xs: 'w-4 h-4', sm: 'w-5 h-5', md: 'w-6 h-6', lg: 'w-8 h-8', xl: 'w-10 h-10', '2xl': 'w-12 h-12',
  };
  return (
    <span className={`inline-flex items-center justify-center ${className}`}>
      <svg className={`${sizeClasses[size]} text-cyan-500`} fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>
    </span>
  );
}

export function RegistrationIcon({ size = 'md', className = '' }) {
  const sizeClasses = {
    xs: 'w-4 h-4', sm: 'w-5 h-5', md: 'w-6 h-6', lg: 'w-8 h-8', xl: 'w-10 h-10', '2xl': 'w-12 h-12',
  };
  return (
    <span className={`inline-flex items-center justify-center relative ${className}`}>
      <UserPlus className={`${sizeClasses[size]} text-emerald-500`} />
    </span>
  );
}

export function InvoiceIcon({ size = 'md', className = '' }) {
  const sizeClasses = {
    xs: 'w-4 h-4', sm: 'w-5 h-5', md: 'w-6 h-6', lg: 'w-8 h-8', xl: 'w-10 h-10', '2xl': 'w-12 h-12',
  };
  return (
    <span className={`inline-flex items-center justify-center relative ${className}`}>
      <CreditCard className={`${sizeClasses[size]} text-violet-500`} />
    </span>
  );
}

/**
 * Get icon component by data type
 */
export function getDataTypeIcon(dataType) {
  const iconMap = {
    children: ChildrenIcon,
    activities: ActivitiesIcon,
    nutrition: NutritionIcon,
    daily_logs: DailyLogsIcon,
    users: UsersIcon,
    takwim: CalendarIcon,
    development: DevelopmentIcon,
    nutritional_status: BMIIcon,
    registrations: RegistrationIcon,
    invoices: InvoiceIcon,
  };
  return iconMap[dataType] || FileText;
}

/**
 * PARENT ICON COMPONENT
 * Modern UI icon to replace 👨‍👩‍👧 emoji
 */
export function ParentIcon({ size = 'md', className = '' }) {
  const sizeClasses = {
    xs: 'w-4 h-4',
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-10 h-10',
    '2xl': 'w-12 h-12',
  };
  
  return (
    <span className={`inline-flex items-center justify-center relative ${className}`}>
      <Users className={`${sizeClasses[size]} text-pink-500`} />
      <Heart className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 text-red-500 fill-red-500" />
    </span>
  );
}

/**
 * TEACHER ICON COMPONENT
 * Modern UI icon to replace 👩‍🏫 emoji
 */
export function TeacherIcon({ size = 'md', className = '' }) {
  const sizeClasses = {
    xs: 'w-4 h-4',
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-10 h-10',
    '2xl': 'w-12 h-12',
  };
  
  const starSizes = {
    xs: 'w-2 h-2',
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-3.5 h-3.5',
    xl: 'w-4 h-4',
    '2xl': 'w-5 h-5',
  };
  
  return (
    <span className={`inline-flex items-center justify-center relative ${className}`}>
      <GraduationCap className={`${sizeClasses[size]} text-emerald-500`} />
      <Star className={`absolute -top-0.5 -right-0.5 ${starSizes[size]} text-amber-400 fill-amber-400`} />
    </span>
  );
}

/**
 * PARENT BADGE - Styled badge with icon
 */
export function ParentBadge({ text = 'Ibu Bapa', variant = 'default' }) {
  const variants = {
    default: 'bg-gradient-to-r from-pink-100 to-purple-100 text-pink-700 border-pink-200',
    solid: 'bg-gradient-to-r from-pink-500 to-purple-500 text-white',
    outline: 'bg-transparent border-2 border-pink-400 text-pink-600',
  };
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold ${variants[variant]}`}>
      <ParentIcon size="sm" />
      {text}
    </span>
  );
}

/**
 * TEACHER BADGE - Styled badge with icon
 */
export function TeacherBadge({ text = 'Pendidik', variant = 'default' }) {
  const variants = {
    default: 'bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-700 border-emerald-200',
    solid: 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white',
    outline: 'bg-transparent border-2 border-emerald-400 text-emerald-600',
  };
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold ${variants[variant]}`}>
      <TeacherIcon size="sm" />
      {text}
    </span>
  );
}

/**
 * COLORFUL FLOATING ELEMENTS
 * Tema: Warna-warni & Ceria!
 */
export function FloatingElements({ variant = 'default' }) {
  const shapes = [
    // Stars
    { type: 'star', left: '5%', top: '15%', color: '#FFE066', size: 35 },
    { type: 'star', left: '88%', top: '25%', color: '#FF6B6B', size: 40 },
    { type: 'star', left: '75%', top: '70%', color: '#4DABF7', size: 30 },
    // Circles
    { type: 'circle', left: '12%', top: '65%', color: '#51CF66', size: 25 },
    { type: 'circle', left: '92%', top: '55%', color: '#BE4BDB', size: 28 },
    { type: 'circle', left: '30%', top: '85%', color: '#FFA94D', size: 22 },
    // Triangles
    { type: 'triangle', left: '80%', top: '12%', color: '#22B8CF', size: 30 },
    { type: 'triangle', left: '20%', top: '40%', color: '#F783AC', size: 25 },
  ];

  const emojis = [
    { emoji: '🎨', left: '8%', top: '25%' },
    { emoji: '🌟', left: '85%', top: '40%' },
    { emoji: '🎈', left: '15%', top: '80%' },
    { emoji: '🎪', left: '78%', top: '85%' },
    { emoji: '🎭', left: '45%', top: '8%' },
    { emoji: '🧸', left: '60%', top: '90%' },
    { emoji: '🎯', left: '35%', top: '75%' },
    { emoji: '🎠', left: '70%', top: '15%' },
  ];

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {/* Colorful Shapes */}
      {shapes.map((shape, i) => (
        <div
          key={`shape-${i}`}
          className="absolute opacity-40"
          style={{
            left: shape.left,
            top: shape.top,
            animation: `floatColorful ${4 + (i % 3)}s ease-in-out infinite`,
            animationDelay: `${i * 0.3}s`,
          }}
        >
          {shape.type === 'star' && (
            <svg width={shape.size} height={shape.size} viewBox="0 0 24 24" fill={shape.color}>
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          )}
          {shape.type === 'circle' && (
            <div style={{ width: shape.size, height: shape.size, backgroundColor: shape.color, borderRadius: '50%' }} />
          )}
          {shape.type === 'triangle' && (
            <div style={{ width: 0, height: 0, borderLeft: `${shape.size/2}px solid transparent`, borderRight: `${shape.size/2}px solid transparent`, borderBottom: `${shape.size}px solid ${shape.color}` }} />
          )}
        </div>
      ))}

      {/* Floating Emojis */}
      {emojis.map((item, i) => (
        <div
          key={`emoji-${i}`}
          className="absolute text-2xl opacity-50"
          style={{
            left: item.left,
            top: item.top,
            animation: `bounceEmoji ${3 + (i % 2)}s ease-in-out infinite`,
            animationDelay: `${i * 0.2}s`,
          }}
        >
          {item.emoji}
        </div>
      ))}

      {/* Rainbow Gradient Blobs */}
      <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-red-300/30 blur-3xl" style={{ animation: 'pulseBlob 4s ease-in-out infinite' }} />
      <div className="absolute top-32 right-20 w-48 h-48 rounded-full bg-blue-300/30 blur-3xl" style={{ animation: 'pulseBlob 5s ease-in-out infinite', animationDelay: '1s' }} />
      <div className="absolute bottom-20 left-1/4 w-44 h-44 rounded-full bg-green-300/30 blur-3xl" style={{ animation: 'pulseBlob 4.5s ease-in-out infinite', animationDelay: '2s' }} />
      <div className="absolute bottom-32 right-1/3 w-36 h-36 rounded-full bg-yellow-300/30 blur-3xl" style={{ animation: 'pulseBlob 3.5s ease-in-out infinite', animationDelay: '0.5s' }} />
      <div className="absolute top-1/2 left-1/2 w-32 h-32 rounded-full bg-purple-300/30 blur-3xl" style={{ animation: 'pulseBlob 4s ease-in-out infinite', animationDelay: '1.5s' }} />

      <style>{`
        @keyframes floatColorful {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          33% { transform: translateY(-20px) rotate(15deg); }
          66% { transform: translateY(-10px) rotate(-10deg); }
        }
        @keyframes bounceEmoji {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-15px) scale(1.1); }
        }
        @keyframes pulseBlob {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}

/**
 * COLORFUL STAT CARD
 */
export function AnimatedStatCard({ 
  icon: Icon, 
  emoji,
  iconComponent: IconComponent, // Allow passing a component like ParentIcon
  label, 
  value, 
  color = 'red',
  delay = 0,
  onClick 
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    if (!isVisible) return;
    const numValue = parseInt(value) || 0;
    if (numValue === 0) { setDisplayValue(0); return; }
    
    let start = 0;
    const increment = numValue / 60;
    const timer = setInterval(() => {
      start += increment;
      if (start >= numValue) {
        setDisplayValue(numValue);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [value, isVisible]);

  const colorConfig = {
    red: { bg: '#FF6B6B', light: '#FFE5E5', border: '#FF6B6B' },
    orange: { bg: '#FFA94D', light: '#FFF3E0', border: '#FFA94D' },
    yellow: { bg: '#FFE066', light: '#FFFDE7', border: '#FFE066' },
    green: { bg: '#51CF66', light: '#E8F5E9', border: '#51CF66' },
    blue: { bg: '#4DABF7', light: '#E3F2FD', border: '#4DABF7' },
    purple: { bg: '#BE4BDB', light: '#F3E5F5', border: '#BE4BDB' },
    pink: { bg: '#F783AC', light: '#FCE4EC', border: '#F783AC' },
    cyan: { bg: '#22B8CF', light: '#E0F7FA', border: '#22B8CF' },
  };

  const config = colorConfig[color] || colorConfig.red;

  return (
    <div
      onClick={onClick}
      className={`
        relative overflow-hidden bg-white rounded-3xl p-5
        shadow-lg hover:shadow-2xl transition-all duration-500 ease-out
        ${onClick ? 'cursor-pointer' : ''}
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        hover:-translate-y-3 hover:rotate-1
        group
      `}
      style={{ 
        transitionDelay: `${delay}ms`,
        border: `4px solid ${config.border}`,
      }}
    >
      {/* Rainbow top bar */}
      <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-red-400 via-yellow-400 via-green-400 via-blue-400 to-purple-400" />
      
      {/* Shimmer effect */}
      <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
      
      {/* Icon */}
      <div 
        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300"
        style={{ backgroundColor: config.light }}
      >
        {IconComponent ? (
          <IconComponent size="xl" />
        ) : emoji ? (
          <span className="text-3xl">{emoji}</span>
        ) : Icon ? (
          <Icon className="w-8 h-8" style={{ color: config.bg }} />
        ) : null}
      </div>
      
      <p className="text-gray-500 text-sm font-bold uppercase tracking-wide mb-1">{label}</p>
      <p className="text-5xl font-black text-gray-800">{displayValue}</p>
      
      {/* Decorative corner */}
      <div 
        className="absolute bottom-0 right-0 w-16 h-16 opacity-10"
        style={{ 
          background: `linear-gradient(135deg, transparent 50%, ${config.bg} 50%)`,
          borderRadius: '0 0 20px 0'
        }}
      />
    </div>
  );
}

/**
 * COLORFUL HEADER
 */
export function AnimatedHeader({ icon, emoji, iconComponent: IconComponent, customIcon, title, subtitle, delay = 0, gradient }) {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  // Default gradient or custom
  const bgGradient = gradient || 'linear-gradient(135deg, #EC4899 0%, #8B5CF6 100%)';

  return (
    <div className={`flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
      <div className="relative flex-shrink-0">
        <div 
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg"
          style={{ 
            background: bgGradient,
            animation: 'wiggleIcon 3s ease-in-out infinite'
          }}
        >
          {customIcon ? (
            customIcon
          ) : IconComponent ? (
            <IconComponent size="lg" className="text-white" />
          ) : emoji ? (
            <span className="text-xl sm:text-2xl">{emoji}</span>
          ) : icon}
        </div>
        {/* Sparkle */}
        <div className="absolute -top-1 -right-1 text-sm sm:text-base" style={{ animation: 'sparkle 1.5s ease-in-out infinite' }}>✨</div>
      </div>
      <div className="min-w-0 flex-1">
        <h1 
          className="text-xl sm:text-2xl lg:text-3xl font-black text-gray-800 truncate"
          style={{ 
            fontFamily: "'Fredoka', sans-serif",
          }}
        >
          <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 bg-clip-text text-transparent">
            {title}
          </span>
        </h1>
        {subtitle && (
          <p className="text-xs sm:text-sm text-gray-500 font-medium truncate">
            {subtitle}
          </p>
        )}
      </div>
      
      <style>{`
        @keyframes wiggleIcon {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-5deg); }
          75% { transform: rotate(5deg); }
        }
        @keyframes sparkle {
          0%, 100% { opacity: 1; transform: scale(1) rotate(0deg); }
          50% { opacity: 0.5; transform: scale(1.3) rotate(180deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * COLORFUL QUICK ACTION BUTTON
 */
export function QuickActionButton({ icon: Icon, emoji, iconComponent: IconComponent, label, sublabel, color = 'red', onClick, delay = 0 }) {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const colorConfig = {
    red: { bg: 'from-red-400 to-red-500', hover: 'hover:from-red-500 hover:to-red-600', shadow: 'shadow-red-300/50' },
    orange: { bg: 'from-orange-400 to-orange-500', hover: 'hover:from-orange-500 hover:to-orange-600', shadow: 'shadow-orange-300/50' },
    yellow: { bg: 'from-yellow-400 to-amber-500', hover: 'hover:from-yellow-500 hover:to-amber-600', shadow: 'shadow-yellow-300/50' },
    green: { bg: 'from-green-400 to-green-500', hover: 'hover:from-green-500 hover:to-green-600', shadow: 'shadow-green-300/50' },
    blue: { bg: 'from-blue-400 to-blue-500', hover: 'hover:from-blue-500 hover:to-blue-600', shadow: 'shadow-blue-300/50' },
    purple: { bg: 'from-purple-400 to-purple-500', hover: 'hover:from-purple-500 hover:to-purple-600', shadow: 'shadow-purple-300/50' },
    pink: { bg: 'from-pink-400 to-pink-500', hover: 'hover:from-pink-500 hover:to-pink-600', shadow: 'shadow-pink-300/50' },
    cyan: { bg: 'from-cyan-400 to-cyan-500', hover: 'hover:from-cyan-500 hover:to-cyan-600', shadow: 'shadow-cyan-300/50' },
  };

  const config = colorConfig[color] || colorConfig.red;

  return (
    <button
      onClick={onClick}
      className={`
        group p-5 rounded-3xl bg-gradient-to-br ${config.bg} ${config.hover}
        transition-all duration-300 text-left text-white
        hover:-translate-y-3 hover:rotate-2 hover:scale-105
        shadow-lg ${config.shadow} hover:shadow-2xl
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
      `}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-14 h-14 rounded-2xl bg-white/30 backdrop-blur-sm flex items-center justify-center mb-3 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg">
        {IconComponent ? (
          <IconComponent size="xl" className="text-white" />
        ) : emoji ? (
          <span className="text-3xl">{emoji}</span>
        ) : Icon ? (
          <Icon className="w-7 h-7 text-white" />
        ) : null}
      </div>
      <p className="font-bold text-lg">{label}</p>
      {sublabel && <p className="text-sm text-white/80">{sublabel}</p>}
    </button>
  );
}

/**
 * COLORFUL GREETING BANNER - TIME-BASED THEME
 * Morning (6am-12pm): Sunrise theme with warm colors
 * Afternoon (12pm-5pm): Bright sunny theme
 * Evening (5pm-8pm): Sunset/dusk theme
 * Night (8pm-6am): Dark night theme with stars
 */
export function GreetingBanner({ userName, role, userPhoto }) {
  const [greeting, setGreeting] = useState('');
  const [emoji, setEmoji] = useState('');
  const [bgColor, setBgColor] = useState('');
  const [isNightTime, setIsNightTime] = useState(false);
  const [tagline, setTagline] = useState('');
  
  useEffect(() => {
    const hour = new Date().getHours();
    
    if (hour >= 6 && hour < 12) {
      // MORNING (6am - 12pm)
      setGreeting('Selamat Pagi');
      setEmoji('🌅');
      setBgColor('from-orange-400 via-yellow-400 to-pink-400');
      setTagline('🌻 Jom mulakan hari dengan semangat!');
      setIsNightTime(false);
    } else if (hour >= 12 && hour < 17) {
      // AFTERNOON (12pm - 5pm)
      setGreeting('Selamat Petang');
      setEmoji('☀️');
      setBgColor('from-yellow-400 via-orange-400 to-red-400');
      setTagline('🎉 Jom belajar sambil bermain!');
      setIsNightTime(false);
    } else if (hour >= 17 && hour < 20) {
      // EVENING/DUSK (5pm - 8pm)
      setGreeting('Selamat Petang');
      setEmoji('🌇');
      setBgColor('from-orange-500 via-pink-500 to-purple-600');
      setTagline('🌷 Semoga hari anda menyenangkan!');
      setIsNightTime(false);
    } else {
      // NIGHT (8pm - 6am)
      setGreeting('Selamat Malam');
      setEmoji('🌙');
      setBgColor('from-slate-800 via-indigo-900 to-purple-900');
      setTagline('⭐ Rehat yang cukup untuk hari esok!');
      setIsNightTime(true);
    }
  }, []);

  // Role icon rendering - use custom icons for ibu_bapa and guru
  const getRoleIcon = (userRole) => {
    if (userRole === 'ibu_bapa') {
      return <ParentIcon size="lg" className="drop-shadow-md" />;
    }
    if (userRole === 'guru') {
      return <TeacherIcon size="lg" className="drop-shadow-md" />;
    }
    const roleEmoji = {
      admin: '👨‍💼',
      superadmin: '👑',
      branch_manager: '🏢',
    };
    return <span className="text-2xl">{roleEmoji[userRole] || '👤'}</span>;
  };

  return (
    <div 
      className={`relative overflow-hidden bg-gradient-to-r ${bgColor} rounded-3xl p-6 mb-6 shadow-xl`}
      style={{ animation: 'slideIn 0.6s ease-out' }}
    >
      {/* Decorative elements based on time */}
      {isNightTime ? (
        <>
          {/* Night theme - Stars */}
          <div className="absolute top-2 right-8 w-2 h-2 bg-white rounded-full animate-pulse" />
          <div className="absolute top-6 right-16 w-1 h-1 bg-yellow-200 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} />
          <div className="absolute top-4 right-32 w-1.5 h-1.5 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.6s' }} />
          <div className="absolute top-10 right-24 w-1 h-1 bg-blue-200 rounded-full animate-pulse" style={{ animationDelay: '0.9s' }} />
          <div className="absolute top-3 right-48 w-2 h-2 bg-yellow-100 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
          <div className="absolute bottom-8 right-12 w-1 h-1 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.7s' }} />
          <div className="absolute bottom-4 right-40 w-1.5 h-1.5 bg-blue-100 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
          {/* Moon glow */}
          <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-100/10 rounded-full blur-xl" />
        </>
      ) : (
        <>
          {/* Day theme - Decorative circles */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/20 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-white/10 rounded-full" />
        </>
      )}
      
      <div className="relative flex items-center gap-4">
        {/* User Photo or Emoji */}
        <div 
          className="relative"
          style={{ animation: 'bounceEmoji 2s ease-in-out infinite' }}
        >
          {userPhoto ? (
            <div className="relative">
              <img 
                src={userPhoto} 
                alt={userName}
                className={`w-16 h-16 rounded-full object-cover border-4 shadow-lg ${isNightTime ? 'border-indigo-300/50' : 'border-white/50'}`}
              />
              <span className="absolute -bottom-1 -right-1 text-2xl">{emoji}</span>
            </div>
          ) : (
            <div className={`w-16 h-16 rounded-full backdrop-blur-sm flex items-center justify-center border-4 shadow-lg ${isNightTime ? 'bg-indigo-500/30 border-indigo-300/50' : 'bg-white/30 border-white/50'}`}>
              <span className="text-4xl">{emoji}</span>
            </div>
          )}
        </div>
        <div className={isNightTime ? 'text-indigo-100' : 'text-white'} style={{ flex: 1 }}>
          <p className={`font-bold ${isNightTime ? 'text-indigo-200' : 'text-white/80'}`}>{greeting}</p>
          <h2 className="text-2xl md:text-3xl font-black flex items-center gap-2">
            {userName} {getRoleIcon(role)}
          </h2>
          <p className={`font-bold mt-1 ${isNightTime ? 'text-indigo-200' : 'text-white/90'}`}>{tagline}</p>
        </div>
      </div>
      
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes bounceEmoji {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-10px) rotate(-5deg); }
          75% { transform: translateY(-5px) rotate(5deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * COLORFUL ANIMATED CARD
 */
export function AnimatedCard({ children, className = '', delay = 0, color = 'red', onClick }) {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const colorConfig = {
    red: '#FF6B6B',
    orange: '#FFA94D',
    yellow: '#FFE066',
    green: '#51CF66',
    blue: '#4DABF7',
    purple: '#BE4BDB',
    pink: '#F783AC',
    cyan: '#22B8CF',
  };

  return (
    <div
      onClick={onClick}
      className={`
        relative overflow-hidden bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6
        shadow-lg hover:shadow-2xl transition-all duration-500 ease-out
        ${onClick ? 'cursor-pointer' : ''}
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        hover:-translate-y-1 sm:hover:-translate-y-2
        ${className}
      `}
      style={{ 
        transitionDelay: `${delay}ms`,
        border: `3px solid ${colorConfig[color]}`,
      }}
    >
      {/* Rainbow top bar */}
      <div className="absolute top-0 left-0 right-0 h-1.5 sm:h-2 bg-gradient-to-r from-red-400 via-yellow-400 via-green-400 via-blue-400 to-purple-400" />
      {children}
    </div>
  );
}

/**
 * COLORFUL CHILD CARD
 */
export function ChildCard({ child, onClick, delay = 0, onPhotoUpload, uploadingPhoto, extra = null }) {
  const [isVisible, setIsVisible] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const activeFrames = useActiveFrames();
  const frames = framesForChild(child, activeFrames);
  const cardFrame = activeFrames?.show_on_cards ? frames[0] : null;
  const colors = ['#FF6B6B', '#FFA94D', '#FFE066', '#51CF66', '#4DABF7', '#BE4BDB'];
  const randomColor = colors[Math.floor(Math.random() * colors.length)];
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const handlePhotoChange = (e) => {
    if (e.target.files[0] && onPhotoUpload) {
      onPhotoUpload(child.id, e.target.files[0]);
    }
  };

  return (
    <div
      onClick={onClick}
      className={`
        relative overflow-hidden p-5 rounded-3xl bg-white
        cursor-pointer hover:shadow-2xl hover:-translate-y-3 hover:rotate-1
        transition-all duration-500
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        group
      `}
      style={{ 
        transitionDelay: `${delay}ms`,
        border: `4px solid ${randomColor}`,
      }}
    >
      {/* Rainbow bar */}
      <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-red-400 via-yellow-400 via-green-400 via-blue-400 to-purple-400" />
      
      {/* Decorative stars */}
      <div className="absolute top-3 right-3 text-xl opacity-0 group-hover:opacity-100 transition-opacity" style={{ animation: 'spinStar 2s linear infinite' }}>⭐</div>
      
      {/* Photo */}
      <div className="relative mx-auto mb-4 w-24 h-24">
        <div 
          className="absolute inset-0 rounded-full blur-lg opacity-50 group-hover:opacity-70 transition-opacity"
          style={{ background: `linear-gradient(135deg, ${randomColor} 0%, #FFE066 100%)` }}
        />
        {cardFrame ? (
          <FramedPhoto
            src={child.photo_url}
            name={child.name}
            frame={cardFrame}
            size={96}
            className="group-hover:scale-110 transition-transform duration-300"
            imgClassName="border-4 border-white shadow-xl"
            fallback={
              <div className="w-24 h-24 rounded-full flex items-center justify-center border-4 border-white shadow-xl" style={{ background: `linear-gradient(135deg, ${randomColor} 0%, #FFE066 100%)` }}>
                <span className="text-4xl">👶</span>
              </div>
            }
          />
        ) : child.photo_url ? (
          <img 
            src={child.photo_url} 
            alt={child.name}
            className="relative w-24 h-24 rounded-full object-cover border-4 border-white shadow-xl group-hover:scale-110 transition-transform duration-300"
          />
        ) : (
          <div 
            className="relative w-24 h-24 rounded-full flex items-center justify-center border-4 border-white shadow-xl group-hover:scale-110 transition-transform duration-300"
            style={{ background: `linear-gradient(135deg, ${randomColor} 0%, #FFE066 100%)` }}
          >
            <span className="text-4xl">👶</span>
          </div>
        )}
        
        {/* Photo upload button */}
        {onPhotoUpload && (
          <label 
            className="absolute bottom-0 right-0 p-2 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white rounded-full cursor-pointer opacity-0 group-hover:opacity-100 transition-all shadow-lg hover:scale-110"
            onClick={(e) => e.stopPropagation()}
          >
            {uploadingPhoto === child.id ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Camera className="w-4 h-4" />
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoChange}
              disabled={uploadingPhoto === child.id}
            />
          </label>
        )}
        
        {/* Online indicator */}
        <div className="absolute bottom-1 right-8 w-5 h-5 bg-green-400 rounded-full border-2 border-white shadow-lg" style={{ animation: 'pulse 2s infinite' }} />
      </div>
      
      {/* Info */}
      <h3 className="font-black text-xl text-gray-800 text-center mb-1">{child.name}</h3>
      <p className="text-sm font-bold text-center" style={{ color: randomColor }}>{child.class_name || 'Kelas belum ditetapkan'}</p>
      <p className="text-xs text-gray-500 text-center mt-1 font-bold">🎂 {child.dob}</p>
      
      {/* Package badge */}
      {child.package && (
        <div className="flex justify-center mt-3">
          <span 
            className="text-xs px-4 py-1.5 rounded-full font-bold text-white"
            style={{ backgroundColor: child.package === 'shift_hour' ? '#4DABF7' : '#BE4BDB' }}
          >
            {child.package === 'shift_hour' ? '⏰ Shift Hour' : '🏢 Office Hour'}
          </span>
        </div>
      )}
      {frames.length > 0 && (
        <div className="flex justify-center mt-3" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={(e) => { e.stopPropagation(); setShareOpen(true); }}
            className="inline-flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-full font-bold text-white bg-pink-500 hover:bg-pink-600 shadow-md hover:shadow-lg transition-[background-color,box-shadow]"
            data-testid={`share-frame-btn-${child.id}`}
          >
            <Share2 className="w-3.5 h-3.5" /> Kongsi Gambar {FRAME_META[frames[0]]?.label}
          </button>
          <ShareFrameDialog open={shareOpen} onOpenChange={setShareOpen} child={child} frames={frames} />
        </div>
      )}
      {extra}
      
      <style>{`
        @keyframes spinStar {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}
